import React, { useEffect, useRef, useState } from 'react';
import { CalendarDays, Pencil, Trash2, Upload } from 'lucide-react';
import { API_ENDPOINTS } from '../../config/constants';
import { generateToken, TOKEN_HEADER } from '../../utils/tokenUtils';
import './MeetupGroup.css';

const PAGE_SIZE = 30;
const EVENT_STATUSES = ['Upcoming', 'Completed', 'Cancelled', 'Archived'];
const EMPTY_EVENT = { event_key: '', name: '', event_date: '', description: '', status: 'Upcoming', source_filename: '' };

function eventKey(name) {
  return name.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

function MeetupEvents() {
  const [events, setEvents] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 0, total: 0 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_EVENT);
  const [formOpen, setFormOpen] = useState(false);
  const fileRef = useRef(null);

  const fetchEvents = async (targetPage = page) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ page: targetPage, limit: PAGE_SIZE });
      if (search.trim()) params.set('search', search.trim());
      if (status) params.set('status', status);
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/admin/meetup-events?${params}`, { headers: { [TOKEN_HEADER]: generateToken() } });
      const payload = await response.json();
      if (!response.ok || !payload.success) throw new Error(payload.message || 'Failed to load Meetup events');
      setEvents(payload.data);
      setPagination(payload.pagination);
      setPage(targetPage);
    } catch (fetchError) {
      setError(fetchError.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEvents(1); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const openForm = (event = null) => {
    setEditing(event);
    setForm(event ? { ...EMPTY_EVENT, ...event, event_date: event.event_date ? String(event.event_date).slice(0, 10) : '' } : EMPTY_EVENT);
    setFormOpen(true);
  };

  const saveEvent = async (submitEvent) => {
    submitEvent.preventDefault();
    const payload = { ...form, event_key: form.event_key || eventKey(form.name) };
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/admin/meetup-events${editing ? `/${editing.id}` : ''}`, {
        method: editing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', [TOKEN_HEADER]: generateToken() },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Failed to save event');
      setFormOpen(false);
      await fetchEvents(editing ? page : 1);
    } catch (saveError) { setError(saveError.message); }
  };

  const deleteEvent = async (event) => {
    if (!window.confirm(`Delete ${event.name} and all its attendee registrations?`)) return;
    const response = await fetch(`${API_ENDPOINTS.BASE_URL}/admin/meetup-events/${event.id}`, { method: 'DELETE', headers: { [TOKEN_HEADER]: generateToken() } });
    const payload = await response.json();
    if (!response.ok || !payload.success) return setError(payload.message || 'Failed to delete event');
    fetchEvents(events.length === 1 && page > 1 ? page - 1 : page);
  };

  const importCSV = async (changeEvent) => {
    const file = changeEvent.target.files?.[0];
    changeEvent.target.value = '';
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    setError('');
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/admin/meetup-events/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'text/csv', 'X-File-Name': encodeURIComponent(file.name), [TOKEN_HEADER]: generateToken() },
        body: await file.text()
      });
      const payload = await response.json();
      if (!response.ok || !payload.success) throw new Error(payload.message || 'Failed to import event');
      setImportResult(payload.data);
      await fetchEvents(1);
    } catch (importError) { setError(importError.message); }
    finally { setImporting(false); }
  };

  return (
    <section className="meetup-group" aria-labelledby="meetup-events-title">
      <div className="meetup-toolbar">
        <div><h2 id="meetup-events-title">Meetup Events</h2><p>{pagination.total} events</p></div>
        <div className="meetup-toolbar-actions">
          <input ref={fileRef} type="file" accept=".csv,text/csv" hidden onChange={importCSV} />
          <button type="button" disabled={importing} onClick={() => fileRef.current?.click()}><Upload size={16} /> {importing ? 'Importing...' : 'Import event CSV'}</button>
          <button className="meetup-primary" type="button" onClick={() => openForm()}><CalendarDays size={16} /> Add event</button>
        </div>
      </div>
      <form className="meetup-filters" onSubmit={(event) => { event.preventDefault(); fetchEvents(1); }}>
        <label><span>Search</span><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Event or filename" /></label>
        <label><span>Status</span><select value={status} onChange={(event) => setStatus(event.target.value)}><option value="">All statuses</option>{EVENT_STATUSES.map((item) => <option key={item}>{item}</option>)}</select></label>
        <button type="submit">Apply</button>
      </form>
      {error && <div className="meetup-error" role="alert">{error}</div>}
      {importResult && <div className="meetup-import-result" role="status"><strong>{importResult.event.name}</strong><span>{importResult.inserted} attendees added</span><span>{importResult.updated} updated</span>{importResult.invalid > 0 && <span>{importResult.invalid} invalid</span>}</div>}
      <div className="meetup-table-wrap">
        <table className="admin-table meetup-table meetup-events-table"><thead><tr><th>Event</th><th>Date</th><th>Status</th><th>Attendees</th><th>Source</th><th aria-label="Actions" /></tr></thead>
          <tbody>
            {!loading && events.map((event) => <tr key={event.id}>
              <td><strong>{event.name}</strong><small>{event.description || event.event_key}</small></td>
              <td>{event.event_date ? String(event.event_date).slice(0, 10) : 'Not set'}</td>
              <td><span className={`meetup-event-status meetup-event-status-${event.status?.toLowerCase()}`}>{event.status}</span></td>
              <td>{event.attendee_count || 0}</td>
              <td><small>{event.source_filename || 'Manual'}</small></td>
              <td className="meetup-actions"><button className="meetup-icon-button" title="Edit event" aria-label={`Edit ${event.name}`} onClick={() => openForm(event)}><Pencil size={16} /></button><button className="meetup-icon-button meetup-danger" title="Delete event" aria-label={`Delete ${event.name}`} onClick={() => deleteEvent(event)}><Trash2 size={16} /></button></td>
            </tr>)}
            {!loading && !events.length && <tr><td colSpan="6" className="meetup-empty">No Meetup events found.</td></tr>}
            {loading && <tr><td colSpan="6" className="meetup-empty">Loading events...</td></tr>}
          </tbody></table>
      </div>
      <div className="meetup-pagination"><button disabled={page <= 1 || loading} onClick={() => fetchEvents(page - 1)}>Previous</button><span>Page {pagination.page} of {Math.max(pagination.pages, 1)}</span><button disabled={page >= pagination.pages || loading} onClick={() => fetchEvents(page + 1)}>Next</button></div>
      {formOpen && <div className="modal-overlay" onMouseDown={(event) => event.target === event.currentTarget && setFormOpen(false)}><div className="modal-content meetup-modal" role="dialog" aria-modal="true"><div className="meetup-modal-header"><h3>{editing ? 'Edit event' : 'Add event'}</h3><button aria-label="Close" onClick={() => setFormOpen(false)}>×</button></div><form onSubmit={saveEvent}><div className="meetup-form-grid">
        <label>Event name<input required value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value, event_key: editing ? form.event_key : eventKey(event.target.value) })} /></label>
        <label>Event key<input required value={form.event_key} onChange={(event) => setForm({ ...form, event_key: event.target.value })} /></label>
        <label>Event date<input type="date" value={form.event_date} onChange={(event) => setForm({ ...form, event_date: event.target.value })} /></label>
        <label>Status<select value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value })}>{EVENT_STATUSES.map((item) => <option key={item}>{item}</option>)}</select></label>
        <label className="meetup-wide">Description<input value={form.description || ''} onChange={(event) => setForm({ ...form, description: event.target.value })} /></label>
      </div><div className="meetup-form-actions"><button type="button" onClick={() => setFormOpen(false)}>Cancel</button><button className="meetup-primary" type="submit">Save event</button></div></form></div></div>}
    </section>
  );
}

export default MeetupEvents;